import React, { useContext, useState } from 'react';
import { SupportVisualisationViews } from 'widgets/supportVisualisation/SupportVisualisationViews';
import { Menu } from '@instinet-analytics/semantic-ui-components';
import './SupportVisualisationMenu.scss';
import AppContext from 'app/AppContext';
import { LogLevel, toLogLevelString } from '@instinet-analytics/core-lib';
import { setGlobalWindowVariable } from 'utils/globalWindowVariables';
import { MeasuredMemory } from 'performance/measuredMemory';
import { LoadedState } from 'widgets/supportVisualisation/LoadState';
import { DownloadScreenshotButton } from 'utils/screenshot/DownloadScreenshotButton';
import classNames from 'classnames';
import { useSupportVisualisationPopupParent } from 'widgets/supportVisualisation/SupportVisualisationPopup/SupportVisualisationPopupService';

export interface SupportVisualisationMenuProps {
    activeView: SupportVisualisationViews;
    onChangeView: (nextView: SupportVisualisationViews) => void;
    loadedState: LoadedState | undefined;
}
export default function SupportVisualisationMenu(
    props: SupportVisualisationMenuProps
) {
    const { activeView, onChangeView, loadedState } = props;

    const popupParent = useSupportVisualisationPopupParent();

    const onClickPopup = () => {
        popupParent && popupParent.openPopup();
    };

    return (
        <Menu pointing vertical className="SupportVisualisationMenu">
            {loadedState && (
                <>
                    <MenuItem view={'overview'} text={'Overview'} />
                    <MenuItem view={'streams'} text={'Streams'} />
                    <MenuItem view={'streamedData'} text={'Streamed Data'} />
                    <MenuItem view={'marketData'} text={'Market Data'} />
                    <MenuItem view={'requests'} text={'Requests'} />
                    <MenuItem view={'messages'} text={'Messages'} />
                    <MenuItem view={'location'} text={'Location'} />
                    <MenuItem view={'layout'} text={'Layout'} />
                    <MenuItem
                        view={'availabilityStatus'}
                        text={'AvailabilityStatus'}
                    />
                    <MenuItem view={'connectivity'} text={'Connectivity'} />
                    <MenuItem view={'viewJson'} text={'View JSON'} />
                    <MenuItem view={'measuredMemory'} text={'Memory'} />
                    <MenuItem
                        view={'embeddedApiMessages'}
                        text={'Embedded API'}
                    />
                    <MenuItem view={'bloombergMessages'} text={'Bloomberg'} />
                    {loadedState?.state?.screenshot && (
                        <div className={'screenshot-button'}>
                            <MenuItem view={'screenshot'} text={'Screenshot'} />
                            <DownloadScreenshotButton
                                fileName={
                                    loadedState!.source + '-screenshot.png'
                                }
                                screenshot={loadedState?.state?.screenshot}
                            />
                        </div>
                    )}
                </>
            )}
            <MenuItem
                className={'bottom'}
                view={'loadJson'}
                text={'Load State'}
            />
            <MenuItem view={'streamtest'} text={'Test Stream'} />
            <MenuItem view={'tradedatestest'} text={'Test Trade Dates'} />
            <MenuItem view={'embeddedApiTest'} text={'Test Embedded Api'} />
            <MenuItem view={'currentFeatureToggles'} text={'Feature Toggles'} />
            <Menu.Item className="menu-loglevel">
                <ToggleLogLevel />
            </Menu.Item>
            <MeasuredMemory />
            <Menu.Item onClick={onClickPopup} className="menu-popup">
                Popup
            </Menu.Item>
        </Menu>
    );

    function MenuItem(props: {
        view: SupportVisualisationViews;
        text: string;
        className?: string;
    }) {
        const { view, text, className } = props;
        const cssClasses = classNames('menu-' + view, className);
        return (
            <Menu.Item
                active={activeView === view}
                onClick={() => onChangeView(view)}
                className={cssClasses}
            >
                {text}
            </Menu.Item>
        );
    }
    function ToggleLogLevel() {
        const {
            services: { loggingService },
        } = useContext(AppContext);

        const [logLevel, setLogLevel] = useState(loggingService.getLogLevel());

        const logLevelDisplay = toLogLevelString(logLevel);

        const toggleLogLevel = () => {
            const nextLogLevel =
                logLevel === LogLevel.Critical ? LogLevel.Debug : logLevel + 1;
            setGlobalWindowVariable({
                logLevel: toLogLevelString(nextLogLevel),
            });
            setLogLevel(nextLogLevel);
        };

        return (
            <div onClick={() => toggleLogLevel()}>
                Log Level: {logLevelDisplay}
            </div>
        );
    }
}
